import api from "./api.service";
import { adminTokenService } from "./admin.token.service";

export interface AdminAction {
    id: number;
    action: string;
    hasPermission: boolean;
}

export interface AdminModule {
    id: number;
    moduleName: string;
    actions: AdminAction[];
}

export interface AdminRole {
    id: number;
    roleName: string;
    status: number;
    isSuperAdmin?: boolean;
    modules: AdminModule[];
}

export interface GetAdminRolesParams {
    page?: number;
    pageSize?: number;
    status?: number;
    SearchString?: string;
}

export interface GetAdminRolesResponse {
    statusCode: number;
    message: string;
    data: AdminRole[];
    pagination: {
        totalCount: number;
        page: number;
        pageSize: number;
        totalPages: number;
    };
}

export interface GetAdminRolesSelectResponse {
    statusCode: number;
    message: string;
    data: {
        id: number;
        roleName: string;
        isSuperAdmin?: boolean;
    }[];
}

export interface UpdateRoleActionsData {
    idRole: number;
    idActions: number[];
}

export interface CreateRoleData {
    name: string;
}

class AdminRoleService {
    /**
     * Creates a new role.
     * @param data Object containing the role name.
     * @returns A promise with the response.
     */
    async createRole(data: CreateRoleData): Promise<{ statusCode: number, message: string }> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.post<{ statusCode: number, message: string }>(
                "/admin/create-role",
                data,
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error creating admin role:", error);
            throw error;
        }
    }

    /**
     * Fetches a list of roles for administration purposes.
     * @param params Filtering and pagination parameters.
     * @returns A promise with the roles data and pagination info.
     */
    async getRoles(params?: GetAdminRolesParams): Promise<GetAdminRolesResponse> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.get<GetAdminRolesResponse>(
                "/admin/get-roles",
                {
                    params,
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching admin roles:", error);
            throw error;
        }
    }

    /**
     * Updates the actions (permissions) assigned to a specific role.
     * @param data Object containing the role ID and the list of action IDs.
     * @returns A promise with the response.
     */
    async updateRoleActions(data: UpdateRoleActionsData): Promise<{ statusCode: number, message: string }> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.post<{ statusCode: number, message: string }>(
                "/admin/update-role-actions",
                data,
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error updating admin role actions:", error);
            throw error;
        }
    }

    /**
     * Fetches a simplified list of active roles for select components.
     * @returns A promise with the roles list.
     */
    async getRolesSelect(): Promise<GetAdminRolesSelectResponse> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.get<GetAdminRolesSelectResponse>(
                "/admin/get-roles-select",
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching admin roles for select:", error);
            throw error;
        }
    }
    /**
     * Performs a logical deletion of a role.
     * @param data Object containing idRole.
     * @returns A promise with the response.
     */
    async deleteRole(data: { idRole: number }): Promise<{ statusCode: number, message: string }> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.post<{ statusCode: number, message: string }>(
                "/admin/delete-role",
                data,
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error deleting admin role:", error);
            throw error;
        }
    }
}

export const adminRoleService = new AdminRoleService();
